import { ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { LlamaIcon } from "~/assets/icons";
import { cn } from "~/lib/utils";

export interface StatusMessageProps {
  title: string;
  body?: string;
  icon?: ReactNode;
  // Only errors are announced to assistive tech as an alert.
  variant?: "error" | "empty";
  action?: ReactNode;
  // Extra classes for the outer container so callers can control how the
  // message fills its slot (full page, panel, inline).
  className?: string;
  "data-testid"?: string;
}

/**
 * Centered icon + title + body block for empty/error states across nextgen.
 * Presentational only — callers supply the copy.
 */
export const StatusMessage = ({
  title,
  body,
  icon,
  variant = "error",
  action,
  className,
  "data-testid": testId,
}: StatusMessageProps) => {
  const DefaultIcon = variant === "error" ? AlertCircle : LlamaIcon;
  return (
    <div
      role={variant === "error" ? "alert" : undefined}
      data-testid={testId}
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground",
        className
      )}
    >
      {icon ?? <DefaultIcon className="size-8" aria-hidden="true" />}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {body && <p className="text-sm">{body}</p>}
      {action}
    </div>
  );
};
